import { Injectable } from '@angular/core';
import { EffectiveFileType } from 'agu_app_shell';
import type { OEPFileKind } from 'agu_oep_core';
import { OEPGraphQLTransport, ReadGraphQLError, type OEPGqlVariables } from './oep-graphql';
import { OEP_CONFIRM_UPLOAD, OEP_DOWNLOAD_URL, OEP_REQUEST_UPLOAD } from './oep-operations';

/** A refusal from OEPFileResolver: `extensions.code` (FORBIDDEN / BAD_USER_INPUT / UPLOAD_MISSING / INTERNAL_ERROR) + its user-facing message. */
export class OEPFileError extends Error {
    constructor(public readonly Code: string, message: string) {
        super(message);
        this.name = 'OEPFileError';
    }
}

interface RequestUploadData { oepRequestUpload: { fileId: string; uploadUrl: string } }
interface ConfirmUploadData { oepConfirmUpload: { fileId: string; name: string; status: string } }
interface DownloadUrlData { oepFileDownloadUrl: string }

/**
 * Draft/final files, server-mediated (apps/MJAPI/src/resolvers/OEPFileResolver.ts):
 * Upload = ticket from the resolver → PUT straight to storage (MJ storage driver's pre-auth
 * upload URL) → confirm. Download = short-lived URL from the resolver. The resolver owns every
 * permission, type and size decision; this only carries bytes. Same three-leg shape as
 * ResearchApiService.UploadDocument.
 */
@Injectable()
export class OEPFilesService {
    constructor(private readonly gql: OEPGraphQLTransport) {}

    async Upload(presentationID: string, kind: OEPFileKind, file: File, onProgress?: (fraction: number) => void): Promise<{ FileID: string }> {
        const contentType = EffectiveFileType(file);
        const ticket = await this.call<RequestUploadData>(OEP_REQUEST_UPLOAD, {
            presentationId: presentationID, kind, fileName: file.name, contentType, sizeBytes: file.size,
        });
        const sent = await this.SendToStorage(ticket.oepRequestUpload.uploadUrl, file, contentType, onProgress);
        if (!sent) throw new OEPFileError('UPLOAD_FAILED', 'The file could not be sent to storage. Try again.');
        await this.call<ConfirmUploadData>(OEP_CONFIRM_UPLOAD, { fileId: ticket.oepRequestUpload.fileId });
        return { FileID: ticket.oepRequestUpload.fileId };
    }

    async DownloadUrl(fileID: string): Promise<string> {
        return (await this.call<DownloadUrlData>(OEP_DOWNLOAD_URL, { fileId: fileID })).oepFileDownloadUrl;
    }

    /**
     * PUT the bytes to the pre-authorized URL. XMLHttpRequest rather than fetch because fetch
     * exposes no upload progress. Azure Blob needs x-ms-blob-type on a SAS PUT. True on 2xx.
     */
    protected SendToStorage(url: string, file: File, contentType: string, onProgress?: (fraction: number) => void): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', url);
            xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
            xhr.setRequestHeader('Content-Type', contentType);
            if (onProgress) xhr.upload.onprogress = (e) => { if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total); };
            xhr.onload = () => resolve(xhr.status >= 200 && xhr.status < 300);
            xhr.onerror = () => resolve(false);
            xhr.onabort = () => resolve(false);
            xhr.send(file);
        });
    }

    private async call<T>(query: string, variables: OEPGqlVariables): Promise<T> {
        try {
            return await this.gql.Execute<T>(query, variables);
        } catch (err) {
            const info = ReadGraphQLError(err);
            if (info === null) throw err;
            throw new OEPFileError(info.Code ?? 'FAILED', info.Message);
        }
    }
}
